// src/react-auth0-spa.js
import createAuth0Client from '@auth0/auth0-spa-js'
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { needsNoAuth0Login } from 'router'
import { hasValidToken } from 'services/user'
import store from 'store'
import { get } from 'utils'

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname)

export const Auth0Context = React.createContext()
export const useAuth0 = () => useContext(Auth0Context)
export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState()
  const [user, setUser] = useState()
  const [auth0Client, setAuth0] = useState()
  const [loading, setLoading] = useState(true)
  const [popupOpen, setPopupOpen] = useState(false)
  const [hasToken, setHasToken] = useState(hasValidToken())
  const [loadingToken, setLoadingToken] = useState(false)

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(initOptions)
      setAuth0(auth0FromHook)
      if (window.location.search.includes('code=')) {
        try {
          const { appState } = await auth0FromHook.handleRedirectCallback()
          onRedirectCallback(appState)
        } catch (e) {
          // console.log("Invalid state on redirect from auth0", e) => Somehow auth0 can not parse the response
          onRedirectCallback({})
        }
      }

      const isAuth = await auth0FromHook.isAuthenticated()
      setIsAuthenticated(isAuth)
      if (isAuth) {
        const u = await auth0FromHook.getUser()
        setUser(u)
      }
      setLoading(false)
    }
    if (!needsNoAuth0Login.includes(get('location.pathname', window, ''))) initAuth0()
    // eslint-disable-next-line
  }, [])

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true)
    try {
      await auth0Client.loginWithPopup(params)
    } catch (error) {
      console.error(error)
    } finally {
      setPopupOpen(false)
    }
    const u = await auth0Client.getUser()
    setUser(u)
    setIsAuthenticated(true)
  }

  const handleRedirectCallback = async () => {
    setLoading(true)
    await auth0Client.handleRedirectCallback()
    const u = await auth0Client.getUser()
    setLoading(false)
    setIsAuthenticated(true)
    setUser(u)
  }

  const getTokenSilently = useCallback((...p) => auth0Client.getTokenSilently(...p), [auth0Client])
  const getIdTokenClaims = useCallback((...p) => auth0Client.getIdTokenClaims(...p), [auth0Client])
  const loginWithRedirect = useCallback((...p) => auth0Client.loginWithRedirect(...p), [
    auth0Client,
  ])
  const getTokenWithPopup = useCallback((...p) => auth0Client.getTokenWithPopup(...p), [
    auth0Client,
  ])
  const logout = useCallback((...p) => auth0Client.logout(...p), [auth0Client])

  // Refresh token from user asynchronously (if needed)
  if (!loading && !hasToken && !loadingToken) {
    setLoadingToken(true)
    getTokenSilently().then((token) => {
      store.set('access_token', token)
      if (!hasToken) setHasToken(true) // to refresh rendering with new token
      setLoadingToken(false)
    })
  }

  return useMemo(
    () => (
      <Auth0Context.Provider
        value={{
          isAuthenticated: isAuthenticated || hasToken, // use stored token for faster loading times (and because token is required for request - auth0.isAuthenticated does not provide token)
          hasToken,
          user,
          loading,
          popupOpen,
          loginWithPopup,
          handleRedirectCallback,
          getIdTokenClaims,
          loginWithRedirect,
          getTokenSilently,
          getTokenWithPopup,
          logout,
        }}
      >
        {children}
      </Auth0Context.Provider>
    ),
    [isAuthenticated, hasToken, user, loading],
  )
}
